import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { campaignsI18n } from './i18n';
import CreateCampaignsModal from './CreateCampaignsModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import CardStats from '../../components/CardStats/CardStats';
import usePermissionsMessaging from 'permissions/modules/messaging/usePermissionsMessaging';

const Campaigns = ({ DesignSystemComponents }) => {
  const {
    Button,
    Card,
    Table,
    Badge,
    IconButton,
    Input,
    Dropdown,
    Modal,
    ComboBox,
  } = DesignSystemComponents;

  const navigate = useNavigate();

  const { t } = useScopedTranslation(campaignsI18n);

  // Mock KPIs
  const totalCampanas = 3;
  const totalDestinatarios = 425;
  const campanasEnviadas = 1;

  // Mock table data
  const campaigns = [
    {
      id: 1,
      nombre: 'Q4 Customer Feedback Survey',
      fecha: '30/11/2024',
      encuesta: 'Customer Satisfaction Survey',
      destinatarios: 150,
      estado: 'Enviado',
    },
    {
      id: 2,
      nombre: 'New Product Launch Announcement',
      fecha: '27/11/2024',
      encuesta: 'Product Feedback Form',
      destinatarios: 75,
      estado: 'Programada',
    },
    {
      id: 3,
      nombre: 'Service Quality Check',
      fecha: '24/11/2024',
      encuesta: 'Service Quality Assessment',
      destinatarios: 200,
      estado: 'Borrador',
    },
  ];

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const { canCreateCampaign, canViewCampaign, canDeleteCampaign } =
    usePermissionsMessaging();

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'Enviado':
        return 'success';
      case 'Programada':
        return 'primary';
      case 'Borrador':
        return 'light';
      default:
        return 'neutral';
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'Enviado':
        return t('status_sent');
      case 'Programada':
        return t('status_scheduled');
      case 'Borrador':
        return t('status_draft');
      default:
        return estado;
    }
  };

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    // TODO: Call API to delete the campaign
    setIsDeleteOpen(false);
    setToDelete(null);
  };

  const parseDate = (value) => {
    if (!value) return null;
    const [day, month, year] = value.split('/');
    return new Date(`${year}-${month}-${day}`);
  };

  const sortedCampaigns = useMemo(() => {
    if (!sortKey || !sortDirection) return campaigns;
    const directionFactor = sortDirection === 'desc' ? -1 : 1;

    return [...campaigns].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];

      if (sortKey === 'fecha') {
        aVal = parseDate(aVal);
        bVal = parseDate(bVal);
      }

      if (aVal === null || aVal === undefined) return -1 * directionFactor;
      if (bVal === null || bVal === undefined) return 1 * directionFactor;

      if (aVal instanceof Date && bVal instanceof Date) {
        return aVal.getTime() > bVal.getTime()
          ? directionFactor
          : aVal.getTime() < bVal.getTime()
            ? -directionFactor
            : 0;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return aVal > bVal
          ? directionFactor
          : aVal < bVal
            ? -directionFactor
            : 0;
      }

      return (
        String(aVal).localeCompare(String(bVal), undefined, {
          sensitivity: 'base',
        }) * directionFactor
      );
    });
  }, [campaigns, sortKey, sortDirection]);

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
  };

  return (
    <div>
      {/* HEADER */}
      <div className='d-flex justify-content-between mb-4'>
        <div>
          <h2 className=' fw-semibold'>{t('campaigns_title')}</h2>
          <h4 className=' fw-normal'>{t('campaigns_subtitle')}</h4>
        </div>

        <Button
          label={t('campaigns_new_campaign')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={() => {
            setEditingCampaign(null);
            setShowCreateModal(true);
          }}
          disabled={!canCreateCampaign}
        />
      </div>

      {/* KPIs */}
      <div className='row mb-4'>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('kpi_total_title')}
            count={totalCampanas}
            subtitle={t('kpi_total_subtitle')}
            icon='clipboard'
          />
        </div>

        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('kpi_recipients_title')}
            count={totalDestinatarios}
            subtitle={t('kpi_recipients_subtitle')}
            icon='users'
          />
        </div>

        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('kpi_sent_title')}
            count={campanasEnviadas}
            subtitle={t('kpi_sent_subtitle')}
            icon='checkCircle'
          />
        </div>
      </div>

      {/* TABLE CARD */}
      <Card specialPadding='p-4'>
        <div className='mb-3'>
          <h3 className='h5 mb-1'>{t('table_title')}</h3>
          <p className='caption font-light mb-0'>{t('table_subtitle')}</p>
        </div>

        <div className='table-container'>
          <Table
            columns={[
              {
                accessor: 'nombre',
                label: t('col_name'),
                sort: true,
              },
              {
                accessor: 'fecha',
                label: t('col_date'),
                sort: true,
              },
              {
                accessor: 'encuesta',
                label: t('col_survey'),
                sort: true,
              },
              {
                accessor: 'destinatarios',
                label: t('col_recipients'),
                sort: true,
              },
              {
                accessor: 'estado',
                label: t('col_status'),
                sort: true,
                render: (row) => (
                  <Badge
                    text={getEstadoLabel(row.estado)}
                    color={getEstadoBadge(row.estado)}
                    size='sm'
                  />
                ),
              },
              {
                label: t('col_actions'),
                render: (row) => (
                  <div className='d-flex gap-2'>
                    {canViewCampaign ? (
                      <Link
                        to={`/campañas/${row.id}`}
                        className='text-decoration-none'
                      >
                        <IconButton
                          ariaLabel={t('aria_view_campaign')}
                          icon='eye'
                          size='md'
                        />
                      </Link>
                    ) : (
                      <IconButton
                        ariaLabel={t('aria_view_campaign')}
                        icon='eye'
                        size='md'
                        disabled
                      />
                    )}

                    <IconButton
                      ariaLabel={t('aria_delete_campaign')}
                      icon='trash'
                      size='md'
                      onClick={() => {
                        setToDelete(row);
                        setIsDeleteOpen(true);
                      }}
                      iconStroke='var(--error-500)'
                      disabled={!canDeleteCampaign}
                    />
                  </div>
                ),
              },
            ]}
            data={sortedCampaigns}
            paginator
            paginationMode='client'
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalItems={sortedCampaigns.length}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
          />
        </div>
      </Card>

      <CreateCampaignsModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        ComboBox={ComboBox}
        Button={Button}
        show={showCreateModal}
        mode={editingCampaign ? 'edit' : 'create'}
        initialData={editingCampaign}
        onClose={() => {
          setShowCreateModal(false);
          setEditingCampaign(null);
        }}
        onSave={(payload) => {
          setShowCreateModal(false);
          setEditingCampaign(null);
          // TODO: refresh campaigns list from API
        }}
        templateOptions={[]}
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('delete_modal_title')}
        message={
          toDelete
            ? t('delete_modal_message', { name: toDelete.nombre })
            : t('delete_modal_message_fallback')
        }
        confirmLabel={t('delete_modal_confirm')}
        confirmVariant='danger'
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />
    </div>
  );
};

export default Campaigns;
